import { X } from 'lucide-react'
import React, { useEffect, useState, useRef } from 'react'

import { cn } from 'src/shared/utils/cn'

import Portal from '../utils/portals/portal'

interface BottomSheetProps {
  open: boolean
  close: () => void
  children: React.ReactNode
  maxHeight?: string
  showCloseButton?: boolean
  backdropOpacity?: number
  className?: string
  outerContent?: React.ReactNode
}

export const BottomSheet = ({
  open,
  close,
  children,
  maxHeight = '90vh',
  showCloseButton = true,
  backdropOpacity = 0.5,
  className,
  outerContent,
}: BottomSheetProps) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStartY, setDragStartY] = useState(0)
  const [dragCurrentY, setDragCurrentY] = useState(0)
  const sheetRef = useRef<HTMLDivElement>(null)
  const backdropRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  // Prevent body scroll when bottom sheet is open
  useEffect(() => {
    if (open) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
      document.body.style.overflow = 'hidden'
      document.body.style.paddingRight = `${scrollbarWidth}px`
    } else {
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
    }

    return () => {
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
    }
  }, [open])

  useEffect(() => {
    let timeoutIdx: NodeJS.Timeout

    if (open) {
      // Reset drag state when opening
      setIsDragging(false)
      setDragStartY(0)
      setDragCurrentY(0)

      setIsVisible(true)

      // Set initial closed position immediately
      if (sheetRef.current) {
        sheetRef.current.style.transform = 'translateY(100%)'
        sheetRef.current.style.transition = 'none'
      }
      if (backdropRef.current) {
        backdropRef.current.style.opacity = '0'
        backdropRef.current.style.transition = 'none'
      }

      // Use requestAnimationFrame to ensure the initial state is rendered
      requestAnimationFrame(() => {
        // Then set up the transition
        if (sheetRef.current) {
          sheetRef.current.style.transition = 'transform 300ms ease-out'
        }
        if (backdropRef.current) {
          backdropRef.current.style.transition = 'opacity 300ms ease-out'
        }

        // Then animate to open position
        requestAnimationFrame(() => {
          if (sheetRef.current) {
            sheetRef.current.style.transform = 'translateY(0)'
          }
          if (backdropRef.current) {
            backdropRef.current.style.opacity = backdropOpacity.toString()
          }
        })
      })
    } else {
      // Reset drag state when closing
      setIsDragging(false)
      setDragStartY(0)
      setDragCurrentY(0)

      if (sheetRef.current) {
        sheetRef.current.style.transition = 'transform 300ms ease-out'
        sheetRef.current.style.transform = 'translateY(100%)'
      }
      if (backdropRef.current) {
        backdropRef.current.style.transition = 'opacity 300ms ease-out'
        backdropRef.current.style.opacity = '0'
      }
      // Wait for animation to complete before hiding
      timeoutIdx = setTimeout(() => {
        setIsVisible(false)
      }, 300)
    }

    return () => {
      clearTimeout(timeoutIdx)
    }
  }, [open, backdropOpacity])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      close()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      close()
    }
  }

  const handleDragStart = (clientY: number, target: EventTarget | null) => {
    // Only start dragging if the user is at the top of the scroll container
    // or if they're dragging from the header area
    if (contentRef.current) {
      const scrollTop = contentRef.current.scrollTop
      const isHeader = target instanceof Element && target.closest('.drag-handle-area')

      if (scrollTop === 0 || isHeader) {
        setIsDragging(true)
        setDragStartY(clientY)
        setDragCurrentY(clientY)
      }
    }
  }

  const handleDragMove = (clientY: number) => {
    if (isDragging && sheetRef.current) {
      const deltaY = clientY - dragStartY

      // Only allow dragging down (positive deltaY)
      if (deltaY > 0) {
        setDragCurrentY(clientY)
        const translateY = Math.max(0, deltaY)
        sheetRef.current.style.transform = `translateY(${translateY}px)`
        sheetRef.current.style.transition = 'none'

        // Update backdrop opacity based on drag distance
        if (backdropRef.current) {
          const opacity = Math.max(0, backdropOpacity - (deltaY / 400) * backdropOpacity)
          backdropRef.current.style.opacity = opacity.toString()
          backdropRef.current.style.transition = 'none'
        }
      }
    }
  }

  const handleDragEnd = () => {
    if (isDragging) {
      const deltaY = dragCurrentY - dragStartY
      const threshold = 150 // pixels to drag before closing

      if (deltaY > threshold) {
        // Close the sheet
        close()
      } else if (sheetRef.current) {
        // Snap back to open position
        sheetRef.current.style.transition = 'transform 300ms ease-out'
        sheetRef.current.style.transform = 'translateY(0)'

        if (backdropRef.current) {
          backdropRef.current.style.transition = 'opacity 300ms ease-out'
          backdropRef.current.style.opacity = backdropOpacity.toString()
        }
      }

      setIsDragging(false)
      setDragStartY(0)
      setDragCurrentY(0)
    }
  }

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    handleDragStart(e.touches[0].clientY, e.target)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    handleDragMove(e.touches[0].clientY)
  }

  const handleTouchEnd = () => {
    handleDragEnd()
  }

  // Mouse event handlers (for desktop)
  const handleMouseDown = (e: React.MouseEvent) => {
    handleDragStart(e.clientY, e.target)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    handleDragMove(e.clientY)
  }

  const handleMouseUp = () => {
    handleDragEnd()
  }

  const handleMouseLeave = () => {
    if (isDragging) {
      handleDragEnd()
    }
  }

  if (!isVisible) {
    return null
  }

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-end justify-center" tabIndex={-1} onKeyDown={handleKeyDown}>
        {/* Backdrop */}
        <div ref={backdropRef} className="absolute inset-0 bg-black" onClick={handleBackdropClick} />
        {outerContent}
        {/* Bottom Sheet */}
        <div
          ref={sheetRef}
          className={cn(
            'relative w-full rounded-t-[30px] bg-white shadow-2xl',
            'flex max-h-[90vh] flex-col',
            className,
          )}
          style={{
            maxHeight,
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {/* Header with Drag Handle and Close Button - Fixed at top */}
          <div className="sticky top-0 z-10 flex flex-row items-center justify-end rounded-t-[30px] p-3 pb-0">
            {/* Close Button */}
            {showCloseButton && (
              <button onClick={close} className="p-2 transition-colors" aria-label="Close">
                <X className="size-5 text-primary" />
              </button>
            )}
          </div>

          {/* Content - Scrollable */}
          <div ref={contentRef} className="overflow-y-auto px-5 pb-10">
            {children}
          </div>
        </div>
      </div>
    </Portal>
  )
}
